export const PRIVACY_POLICY_EFFECTIVE_DATE = "6 September 2026";
export const PRIVACY_POLICY_TITLE = "Privacy Policy";
export const PRIVACY_POLICY_PUBLIC_ORIGIN = "https://tradivo.com.au";
export const PRIVACY_POLICY_CANONICAL_URL = "https://tradivo.com.au/privacy";
export const PRIVACY_OPERATOR_NAME = "Fugs Pty Ltd";
export const PRIVACY_CONTACT_EMAIL = "privacy@tradivo.com.au";
export const PRIVACY_POSTAL_ADDRESS_LINES = [
  "PO Box 46",
  "Smithfield West LPO",
  "NSW 2164",
  "Australia",
] as const;

export type PrivacyBlock =
  | { type: "p"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "callout"; text: string }
  | { type: "email"; email: string }
  | { type: "address"; lines: readonly string[] };

export type PrivacySection = {
  id: string;
  title: string;
  blocks: PrivacyBlock[];
};

export const PRIVACY_SECTIONS: PrivacySection[] = [
  {
    id: "who-we-are",
    title: "Who we are",
    blocks: [
      {
        type: "p",
        text: "This Privacy Policy describes how Tradivo (“Tradivo”, “we”, “us”) handles personal information. Tradivo is an Australian commerce operations platform used by businesses to manage products, inventory, purchasing, sales, warehouse work, listings, and marketplace connections (including eBay and BigCommerce).",
      },
      {
        type: "p",
        text: "The public website is published at https://tradivo.com.au. This policy’s canonical public URL is https://tradivo.com.au/privacy.",
      },
      {
        type: "p",
        text: "Tradivo is operated by Fugs Pty Ltd. Privacy enquiries may be sent to privacy@tradivo.com.au. Postal notices may be sent to PO Box 46, Smithfield West LPO, NSW 2164, Australia.",
      },
      {
        type: "p",
        text: "This document is a privacy notice for the service as built. It is not a certification of compliance with every privacy law worldwide, and it is not legal advice.",
      },
    ],
  },
  {
    id: "scope",
    title: "Scope of this policy",
    blocks: [
      {
        type: "p",
        text: "This policy applies to personal information handled when you visit public pages of the service (including this page), create or use a Tradivo account, join an organisation, or when a business uses Tradivo to store operational records.",
      },
      {
        type: "p",
        text: "It does not replace a merchant’s own privacy policy for that merchant’s customers or website. Marketplace operators (including eBay and BigCommerce) have their own policies for accounts on those platforms.",
      },
    ],
  },
  {
    id: "information-we-collect",
    title: "Information we collect",
    blocks: [
      {
        type: "p",
        text: "Depending on how the service is used, we may collect and hold the following kinds of personal information. We do not collect kinds of information that the product does not support.",
      },
      {
        type: "p",
        text: "A. Tradivo account and user information: email address, password (held by the authentication provider as a credential, not as a recoverable password in application tables), display name, organisation membership and role, invitation email and invitation status, profile fields such as avatar URL if supplied, and sign-in / password-reset activity.",
      },
      {
        type: "p",
        text: "B. Merchant or business information: organisation name, settings (currency, timezone, industry where entered), user lists, supplier records (name, email, codes and related purchasing contacts where entered), and operational configuration.",
      },
      {
        type: "p",
        text: "C. Merchant customer and end-buyer information (held because a business uses Tradivo): customer name, email, addresses, order and return records, shipment and tracking details, and similar fulfilment data. Merchants may also upload product and listing media (usually product imagery, which may incidentally include personal information). This is typically the merchant’s customer or catalogue information, not information we collect for our own marketing of those individuals.",
      },
      {
        type: "p",
        text: "D. Marketplace and provider account information: connector identifiers, seller or store identifiers (including eBay seller_user_id when bound), OAuth or API tokens stored as encrypted connector secrets, listing and imported-order metadata, and marketplace account-deletion notice records described later.",
      },
      {
        type: "p",
        text: "E. Technical, security and audit information: organisation-scoped audit events, authentication session tokens in the browser, local preferences, IP-related metadata that infrastructure logs may record, and limited AI usage logs (task, template version, token usage estimates, success/failure) rather than a permanent store of full sensitive prompts.",
      },
    ],
  },
  {
    id: "how-we-collect",
    title: "How we collect information",
    blocks: [
      {
        type: "p",
        text: "We collect information:",
      },
      {
        type: "ul",
        items: [
          "directly from you, when you register, sign in, update a profile, invite a teammate, or enter operational records;",
          "from the organisation that invited you or that stores records about its customers and suppliers;",
          "from marketplaces and carriers when a merchant connects those services (for example OAuth, imported orders, listings, tracking);",
          "from eBay when it sends a signed Marketplace Account Deletion notification to our compliance endpoint;",
          "automatically, as session, preference, and security data in the browser and in application logs.",
        ],
      },
      {
        type: "p",
        text: "We do not scrape unrelated third-party profiles for this service. If you provide someone else’s information (for example a customer address), you must have a lawful basis and any required notice to that person.",
      },
    ],
  },
  {
    id: "why-we-use",
    title: "Why we use personal information",
    blocks: [
      {
        type: "p",
        text: "We collect, hold, use and disclose personal information to:",
      },
      {
        type: "ul",
        items: [
          "provide the platform, authenticate users, and enforce organisation and role access;",
          "let merchants manage products, inventory, purchasing, sales, warehouse work, listings and integrations;",
          "import and fulfil marketplace orders the merchant has authorised;",
          "send operational messages such as invitations and password-reset emails;",
          "maintain security, audit trails, troubleshooting, and abuse prevention;",
          "meet legal obligations, including responding to marketplace account-deletion notifications;",
          "improve reliability of the service, including limited product-content drafting assistance where a merchant uses that feature.",
        ],
      },
      {
        type: "p",
        text: "We do not sell personal information. We do not use merchant customer lists to market Tradivo to those customers.",
      },
    ],
  },
  {
    id: "marketplace-integrations",
    title: "Marketplace and third-party integrations",
    blocks: [
      {
        type: "p",
        text: "When a merchant connects eBay or BigCommerce, Tradivo exchanges information with those platforms using credentials the merchant authorises. That can include seller identity, listings, inventory quantities, and order or fulfilment data the merchant has permission to access.",
      },
      {
        type: "p",
        text: "Tokens and connector secrets are stored using application encryption routines and are not intended to be readable in the browser. Access is organisation-scoped. Least-privilege scopes and host allow-lists are used where the integration architecture requires them.",
      },
      {
        type: "p",
        text: "eBay and BigCommerce process information under their own terms and privacy policies. Merchants remain responsible for configuring those accounts and for their obligations to their own customers.",
      },
    ],
  },
  {
    id: "merchant-customer-information",
    title: "Merchant and customer information",
    blocks: [
      {
        type: "p",
        text: "When a business uses Tradivo to manage information about its customers, that business may have its own privacy obligations and may determine why that information is processed. Tradivo provides software and hosting so the business can operate. Merchant privacy policies may also apply to those individuals.",
      },
      {
        type: "p",
        text: "If you are an end-buyer of a merchant and you want access, correction or deletion of information that merchant stored in Tradivo, start with that merchant. We can assist the organisation that holds the account, subject to authentication, legal limits, and the architecture described in this policy.",
      },
    ],
  },
  {
    id: "cookies",
    title: "Cookies and similar technologies",
    blocks: [
      {
        type: "p",
        text: "The service uses the browser’s local storage (and related session persistence provided by the authentication library) to keep you signed in, remember the selected organisation, store invitation tokens during sign-up, and store local UI preferences. These are necessary for the application to function.",
      },
      {
        type: "p",
        text: "This Privacy Policy page does not load a third-party analytics script. We have not introduced advertising cookies for this page. If we later add optional analytics, we will update this section.",
      },
    ],
  },
  {
    id: "disclosure",
    title: "How we disclose and share information",
    blocks: [
      {
        type: "p",
        text: "We disclose personal information only as needed to operate the service or as required by law, including to:",
      },
      {
        type: "ul",
        items: [
          "other members of the same organisation, according to roles and permissions;",
          "infrastructure and email providers that host or send messages for the service;",
          "marketplaces and carriers the merchant has connected;",
          "professional advisers or authorities where we reasonably believe disclosure is required;",
          "a successor operator if the service is transferred, with appropriate continuity of this notice.",
        ],
      },
    ],
  },
  {
    id: "service-providers",
    title: "Service providers and subprocessors",
    blocks: [
      {
        type: "p",
        text: "We use specialist providers to run the product. As implemented, that includes:",
      },
      {
        type: "ul",
        items: [
          "operator-controlled web servers that serve the public site at https://tradivo.com.au, including an OVH virtual private server; this policy does not name a production data-centre country or cloud region for that hosting;",
          "Supabase for authentication, database, storage, and Edge Functions;",
          "eBay and BigCommerce when a merchant connects those marketplaces;",
          "carrier systems (such as DHL, FedEx or UPS) only if the merchant configures those connectors;",
          "transactional email delivery for invitations and authentication mail, where that function is enabled.",
        ],
      },
      {
        type: "p",
        text: "Providers only receive what is needed to perform their role. We do not list every possible future vendor here; material changes will be reflected when this policy is updated.",
      },
    ],
  },
  {
    id: "overseas",
    title: "Overseas disclosures and international processing",
    blocks: [
      {
        type: "p",
        text: "Yes — we are likely to disclose personal information to overseas recipients (APP 1.4(f)–(g)). Marketplace operators and some infrastructure providers used to run the service are located outside Australia. Recipients may process information in countries including the United States, where those operators or providers are based. The public website is served from operator-controlled servers; this policy does not name a production hosting country or cloud region for that deployment. It is not always practicable to name every country in advance because a merchant’s chosen integrations and a provider’s published regions can change.",
      },
      {
        type: "p",
        text: "Where Australian Privacy Principle 8 applies, we take the steps that are reasonable in the circumstances so that overseas recipients do not breach the Australian Privacy Principles, noting that some disclosures are required to use a marketplace the merchant has chosen.",
      },
    ],
  },
  {
    id: "security",
    title: "Data security",
    blocks: [
      {
        type: "p",
        text: "We take reasonable administrative, technical and organisational steps to protect personal information. Proven in this architecture, those steps include authentication, organisation-scoped access, row-level security in the database, encrypted storage of connector secrets, least-privilege integration patterns, and audit events for significant actions. Edge Functions that talk to marketplaces run with server-side secrets, not in the public browser bundle.",
      },
      {
        type: "p",
        text: "No method of transmission or storage is completely secure. We do not claim that information is encrypted in every possible state, and we do not claim a named security certification unless we publish one separately.",
      },
    ],
  },
  {
    id: "retention",
    title: "Data retention",
    blocks: [
      {
        type: "p",
        text: "We keep personal information for as long as it is needed for the purposes in this policy, including providing the service to the organisation, security and audit, dispute handling, and legal or accounting obligations. We have not published a single fixed retention period for every table because operational and legal needs differ (for example orders versus a failed login).",
      },
      {
        type: "p",
        text: "When information is no longer required, we take steps that are reasonable in the circumstances to destroy or de-identify it, subject to backups and the deletion limits described below.",
      },
    ],
  },
  {
    id: "access-correction",
    title: "Access and correction",
    blocks: [
      {
        type: "p",
        text: "You may request access to personal information we hold about you, and you may ask us to correct it if it is inaccurate, out of date, incomplete, irrelevant or misleading (APPs 12 and 13).",
      },
      {
        type: "p",
        text: "Account users can often update profile and organisation data inside the product. For other requests, contact us using the details in Contact us. We may need to verify your identity. If we refuse access or correction, we will explain why, except where the Privacy Act allows us not to.",
      },
    ],
  },
  {
    id: "choices",
    title: "Privacy choices",
    blocks: [
      {
        type: "p",
        text: "You can decline to provide information that is not required, but some features will not work without an account or without records a merchant chooses to store. You can sign out, request account closure, and control organisation membership through administrators. Browser storage can be cleared, which signs you out.",
      },
    ],
  },
  {
    id: "direct-marketing",
    title: "Direct marketing",
    blocks: [
      {
        type: "p",
        text: "We send operational messages (invitations, password reset, service-security notices). The product does not currently operate a general marketing mailing list of merchant customers. If we use personal information for direct marketing in future, we will provide a simple way to opt out, consistent with APP 7.",
      },
    ],
  },
  {
    id: "account-closure",
    title: "Account closure and deletion requests",
    blocks: [
      {
        type: "p",
        text: "Organisation administrators can remove members. Closing an organisation or deleting an account is a governed process: we will action a verified request, subject to legal obligations, legitimate retention (including tax, dispute and security records), contractual duties, and technical backup cycles. Deletion is not always instantaneous and may not remove every historical audit artefact immediately.",
      },
      {
        type: "p",
        text: "We will not use backup or audit language to avoid a deletion we are required to perform. We also will not promise a complete instant wipe of every related commerce record if the system is not built to do that yet.",
      },
    ],
  },
  {
    id: "ebay-account-deletion",
    title: "eBay Marketplace Account Deletion",
    blocks: [
      {
        type: "p",
        text: "Tradivo operates a verified HTTPS endpoint for eBay Marketplace Account Deletion / closure notifications. eBay can challenge that endpoint and can send signed notifications. Tradivo verifies signatures using eBay’s published public keys, records a minimised compliance ledger (notification identifiers, hashed username where provided, payload hash — not the raw notification body, username in clear text, or eIAS token), matches connected sellers by stable eBay seller_user_id only, and places matched connectors on a governed erasure queue.",
      },
      {
        type: "p",
        text: "That endpoint does not, today, automatically cascade-delete orders, customers, connector secrets, or all historic records. When a verified deletion notice is received, Tradivo processes it subject to legal obligations, legitimate retention, fraud, security and audit requirements, contractual obligations, and technical backup cycles. Further erasure steps are a separate governed workflow, not an instantaneous wipe of the entire tenant.",
      },
    ],
  },
  {
    id: "data-breaches",
    title: "Data breaches",
    blocks: [
      {
        type: "p",
        text: "If we become aware of a data breach, we will assess it and, where the Notifiable Data Breaches scheme in the Privacy Act applies, notify the Australian Information Commissioner and affected individuals as required. Merchants may also have their own notification duties for information they control.",
      },
    ],
  },
  {
    id: "children",
    title: "Children",
    blocks: [
      {
        type: "p",
        text: "Tradivo is a business operations product. It is not directed at children. We do not knowingly create accounts for people under 18. If you believe we have collected information from a child, contact us and we will take reasonable steps to delete it.",
      },
    ],
  },
  {
    id: "automated-processing",
    title: "Automated processing and AI",
    blocks: [
      {
        type: "p",
        text: "The product can assist merchants with drafting product marketing content using a bounded AI task and logged usage metadata. That assistance operates on product content the merchant supplies. We do not currently arrange for a computer program to use personal information to make, or substantially and directly make, decisions that could reasonably be expected to significantly affect an individual’s rights or interests (for example credit, tenancy, employment or access to an essential service).",
      },
      {
        type: "p",
        text: "From 10 December 2026, Australian Privacy Principles 1.7–1.9 will require extra privacy-policy detail if we do use personal information in that kind of significant automated decision-making. If that changes, we will update this section with the kinds of personal information used and the kinds of decisions involved. Ordinary application logic (validation, role checks, inventory arithmetic) is not described here as significant ADM.",
      },
    ],
  },
  {
    id: "international-users",
    title: "International users",
    blocks: [
      {
        type: "p",
        text: "Tradivo is designed first for Australian organisations. If you are outside Australia, additional rights may apply depending on where you live. We do not claim that the service is certified under the GDPR, UK GDPR or California law. We have not appointed an EU representative or a Data Protection Officer under those regimes.",
      },
      {
        type: "p",
        text: "Where an applicable law gives you rights of access, correction, deletion, restriction, objection, portability or withdrawal of consent, you may ask us to honour them. We will consider the request in line with that law and with this policy, including the merchant-controlled nature of much customer data.",
      },
    ],
  },
  {
    id: "complaints",
    title: "Complaints",
    blocks: [
      {
        type: "p",
        text: "If you believe we have breached the Australian Privacy Principles, contact us first using Contact us. Please include enough detail for us to investigate. We will acknowledge a privacy complaint and aim to respond within a reasonable period.",
      },
      {
        type: "p",
        text: "If you are not satisfied, you may complain to the Office of the Australian Information Commissioner (OAIC). See oaic.gov.au for current contact methods. You may also have rights with a privacy regulator in your own country.",
      },
    ],
  },
  {
    id: "changes",
    title: "Changes to this policy",
    blocks: [
      {
        type: "p",
        text: "We may update this policy as the product or the law changes. The effective date at the top of the page will change when we do. Material changes should be readable at https://tradivo.com.au/privacy. Continued use of the service after an update means the revised policy applies to later handling of information.",
      },
    ],
  },
  {
    id: "contact",
    title: "Contact us",
    blocks: [
      {
        type: "p",
        text: `For privacy enquiries about Tradivo, contact ${PRIVACY_OPERATOR_NAME}, the legal operator of the service.`,
      },
      {
        type: "email",
        email: PRIVACY_CONTACT_EMAIL,
      },
      {
        type: "address",
        lines: PRIVACY_POSTAL_ADDRESS_LINES,
      },
    ],
  },
];
